import { Package, Zap, Wrench, Globe, Cpu, Layers, Building, Truck, Shield, Crosshair, Gauge, Ruler, Radio, Eye, Thermometer, Wind, Plane, Radar, Weight, Maximize2, BatteryCharging, Navigation, Monitor, Users, Waves, Mountain, Anchor, Compass, Timer, Activity, ScanLine, Wifi } from "lucide-react";
import { SectionHeader } from "@/components/site/SectionHeader";
import { ContactBanner } from "@/components/site/ContactBanner";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, ImageReveal, PageTransition,
} from "@/components/site/Animations";

const DRONE_IMAGE = "/ketzal_interceptor.jpg";
const DRONE_VIDEO = "https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/uvqut5vb_795035985.034435-285545139.mp4";

const UAS_VIDEO = "https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/um65yjhw_795038227.889844-1981103592.mp4";
const UAS_MAIN_IMAGE = "https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/dt34e89c_image.png";

const DRONE_SPECS = [
  { icon: Gauge, label: "Max Dash Speed", value: "300-400 km/h", detail: "248 mph in optimal conditions" },
  { icon: Crosshair, label: "Operational Range", value: "~50 km", detail: "32 miles intercept radius" },
  { icon: Ruler, label: "Length", value: "0.3 m (1 ft)", detail: "Compact quad-rotor form factor" },
  { icon: Radio, label: "Control System", value: "Dual-Band 915MHz / 2.4GHz", detail: "T-style diversity antenna for extended range" },
  { icon: Eye, label: "Camera", value: "720p HD FPV", detail: "1/1.8\" CMOS, Starlight capable, 155-165° FOV" },
  { icon: Thermometer, label: "Operating Temp", value: "-10°C to +40°C", detail: "All-weather operational capability" },
  { icon: Wind, label: "Wind Resistance", value: "Level 8 Beaufort", detail: "Stable flight up to 74 km/h winds" },
];


const JAGWAR_IMAGE = "https://customer-assets.emergentagent.com/job_850b2bbd-f5de-4a32-9b3c-f8fba4c02ca2/artifacts/pvsoqhg6_image.png";
const TIBRONE_IMAGE = "https://customer-assets.emergentagent.com/job_850b2bbd-f5de-4a32-9b3c-f8fba4c02ca2/artifacts/6z964jrt_image.png";

const JAGWAR_SPECS = [
  { icon: Gauge, label: "Max Speed", value: "45 km/h", detail: "All-terrain autonomous mobility" },
  { icon: Crosshair, label: "Operational Range", value: "30 km", detail: "Extended patrol and strike radius" },
  { icon: Weight, label: "Platform Weight", value: "120 kg", detail: "Man-portable modular configuration" },
  { icon: Eye, label: "Sensors", value: "Multi-Spectral", detail: "Thermal, LIDAR, and optical fusion" },
  { icon: Radio, label: "Comms", value: "Mesh Network", detail: "Encrypted multi-node relay system" },
  { icon: Thermometer, label: "Operating Temp", value: "-20°C to +55°C", detail: "Desert to arctic operational range" },
  { icon: ScanLine, label: "Stealth Profile", value: "Low Observable", detail: "Reduced acoustic and thermal signature" },
  { icon: BatteryCharging, label: "Endurance", value: "48+ hours", detail: "Extended sentinel mode capability" },
];

const TIBRONE_SPECS = [
  { icon: Gauge, label: "Max Speed", value: "55 knots", detail: "High-speed intercept capability" },
  { icon: Crosshair, label: "Patrol Range", value: "200 nm", detail: "Extended autonomous patrol radius" },
  { icon: Weight, label: "Displacement", value: "2,500 kg", detail: "Semi-submersible hull configuration" },
  { icon: Ruler, label: "Length", value: "7 m (23 ft)", detail: "Low-profile surface vessel" },
  { icon: Radar, label: "Detection", value: "Multi-Domain", detail: "Sonar, radar, and EO/IR integration" },
  { icon: Anchor, label: "Sea State", value: "Up to SS 5", detail: "Operational in rough sea conditions" },
  { icon: Navigation, label: "Navigation", value: "GNSS + INS", detail: "GPS-denied environment capable" },
  { icon: Timer, label: "Endurance", value: "72+ hours", detail: "Autonomous persistent patrol" },
];

const OTHER_PRODUCTS = [
  {
    icon: Zap,
    category: "Energy",
    title: "Energy Infrastructure Components",
    description: "Procurement support for grid-adjacent infrastructure, alternative fuel systems, and industrial-scale energy output components.",
    items: [
      "Grid resilience infrastructure",
      "Alternative fuel pathway systems",
      "Industrial-scale energy components",
    ],
  },
  {
    icon: Wrench,
    category: "Manufacturing",
    title: "Advanced Manufacturing Systems",
    description: "Prefabrication, modular systems, and rapid-deployment manufacturing capabilities for time-sensitive program requirements.",
    items: [
      "Prefabricated building systems",
      "Modular manufacturing solutions",
      "Rapid-deployment infrastructure",
    ],
  },
  {
    icon: Globe,
    category: "Sourcing",
    title: "Strategic Sourcing Programs",
    description: "End-to-end procurement strategy development including partner architecture, qualification, and delivery assurance frameworks.",
    items: [
      "Partner architecture development",
      "Supplier qualification programs",
      "Delivery assurance frameworks",
    ],
  },
  {
    icon: Cpu,
    category: "Technology",
    title: "Emerging Technology Integration",
    description: "Technical diligence and procurement readiness for novel systems and technology-enabled material applications.",
    items: [
      "Novel systems evaluation",
      "Technology-enabled materials",
      "Technical feasibility assessment",
    ],
  },
  {
    icon: Layers,
    category: "Supply Chain",
    title: "Supply Chain Architecture",
    description: "Design and management of resilient supply networks across regulated, cross-border, and security-constrained environments.",
    items: [
      "Cross-border supply chain design",
      "Regulatory compliance management",
      "Supply chain resilience planning",
    ],
  },
  {
    icon: Building,
    category: "Infrastructure",
    title: "Built Environment Solutions",
    description: "Procurement coordination for building materials, construction supply chains, and infrastructure delivery programs.",
    items: [
      "Building material procurement",
      "Construction supply coordination",
      "Infrastructure delivery support",
    ],
  },
];

export default function FeaturedPage() {
  const [showVideo, setShowVideo] = useState(false);
  const [showUasVideo, setShowUasVideo] = useState(false);

  return (
    <PageTransition>
    <div data-testid="featured-page">
      {/* Page Header */}
      <section className="bg-white pt-10 sm:pt-12 lg:pt-16 pb-10">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          <FadeIn>
          <SectionHeader
            eyebrow="Offerings"
            title="Featured Products & Services"
            description="High-demand procurement opportunities, technologies, and industrial capabilities exclusive to the Cortes Industries network. These offerings represent proprietary and partner-sourced solutions where our operational expertise and supply chain architecture deliver structured, accountable outcomes."
          />
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[hsl(218,60%,10%)] text-white rounded-md badge-glow">
            <Shield className="w-4 h-4 text-[hsl(41,47%,61%)]" />
            <span className="text-xs font-semibold tracking-wide uppercase" style={{ fontFamily: 'var(--font-display)' }}>
              Exclusive to Cortes Industries
            </span>
          </div>
          </FadeIn>
        </div>
      </section>

      <RevealLine />

      {/* ====== FEATURED PRODUCT 1: ADEVOLEX DRONE ====== */}
      <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="featured-product-adevolex">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          {/* Product Header */}
          <FadeIn>
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-8 bg-[hsl(41,47%,61%)] rounded-sm" />
              <p
                className="text-xs font-semibold tracking-[0.14em] uppercase text-[hsl(41,47%,61%)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Featured Product
              </p>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Ketzal 7HSR FPV Interceptor-Defender Drone
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-3xl">
              A compact, high-performance quad-rotor interceptor-defender system designed to chase and collide with enemy strike drones. The Ketzal is built for long-range tactical reconnaissance and high-speed aerial operations in complex, contested environments.
            </p>
          </div>
          </FadeIn>

          {/* Name Origin */}
          <FadeIn delay={0.12}>
          <div className="mb-10 max-w-3xl border-l-2 border-[hsl(41,47%,61%)] pl-5">
            <p className="text-base font-semibold tracking-tight text-foreground italic" style={{ fontFamily: 'var(--font-display)' }}>
              Ketzal: Guardian of the Skies
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Air (interceptor/defender) — The "Quetzal" is the sacred bird of Mesoamerica. Tied to "Quetzalcoatl", the feathered serpent god — a protector deity. Bright, agile, hard to catch.
            </p>
          </div>
          </FadeIn>

          {/* AI / Human Supervised Badge */}
          <FadeIn delay={0.15}>
          <div className="mb-10 flex flex-col sm:flex-row gap-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-secondary border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                AI-Driven & Autonomous
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-secondary border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                Fully Human Supervised
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-[hsl(218,60%,10%)] rounded-lg">
              <Shield className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-display)' }}>
                Exclusive to Cortes Industries
              </span>
            </div>
          </div>
          </FadeIn>

          {/* Image & Video Grid */}
          <FadeIn delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Product Image */}
            <div className="rounded-lg overflow-hidden border border-border bg-black">
              <img
                src={DRONE_IMAGE}
                alt="Ketzal 7HSR FPV Interceptor-Defender Drone"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Video */}
            <div className="rounded-lg overflow-hidden border border-border bg-black relative">
              {showVideo ? (
                <video
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                  data-testid="adevolex-video"
                >
                  <source src={DRONE_VIDEO} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div
                  className="w-full h-full min-h-[300px] lg:min-h-0 flex flex-col items-center justify-center cursor-pointer group"
                  onClick={() => setShowVideo(true)}
                  data-testid="adevolex-video-trigger"
                >
                  <div className="w-16 h-16 rounded-full bg-[hsl(41,47%,61%)] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-[hsl(218,60%,10%)] ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-white/80" style={{ fontFamily: 'var(--font-display)' }}>
                    Watch Demonstration Video
                  </p>
                  <p className="text-xs text-white/50 mt-1">Click to play</p>
                </div>
              )}
            </div>
          </div>
          </FadeIn>

          {/* Technical Specifications */}
          <FadeIn delay={0.1}>
          <div className="mb-12">
            <h3
              className="text-lg font-semibold tracking-tight text-foreground mb-6"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Technical Specifications
            </h3>
            <StaggerContainer stagger={0.06} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
              {DRONE_SPECS.map((spec) => (
                <StaggerItem key={spec.label} className="flex">
                <div className="border border-border rounded-lg p-4 bg-white spec-shimmer card-hover flex-1 flex flex-col">
                  <div className="flex items-center gap-2.5 mb-2">
                    <spec.icon className="w-4 h-4 text-[hsl(41,47%,61%)]" />
                    <span className="text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                      {spec.label}
                    </span>
                  </div>
                  <p className="text-base font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                    {spec.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{spec.detail}</p>
                </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
          </FadeIn>

          {/* Key Capabilities */}
          <FadeIn delay={0.1}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3
                className="text-lg font-semibold tracking-tight text-foreground mb-4"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                System Capabilities
              </h3>
              <ul className="space-y-3">
                {[
                  "Compact quad-rotor designed to chase and collide with enemy drones",
                  "Reportedly engaged hundreds of targets in operational environments",
                  "7-inch carbon fiber frame optimized for extreme velocity",
                  "High-torque brushless motors compatible with 8S LiPo systems",
                  "Enhanced resistance to multi-path interference and signal jamming",
                  "720p HD FPV camera with Starlight low-light capability and 155-165° wide-angle lens",
                  "Cruise altitude of 5,000m (16,400 ft)",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3
                className="text-lg font-semibold tracking-tight text-foreground mb-4"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                AI Terminal Guidance Module
              </h3>
              <div className="p-5 bg-secondary/60 rounded-lg border border-border mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                    AI-Driven, Fully Human Supervised
                  </p>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  The optional AI Terminal Guidance Module enables autonomous target recognition and automated terminal flight correction in electronic warfare (EW) contested environments. While autonomous in execution, all operations remain under full human supervision and authorization.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "Autonomous target recognition and tracking",
                  "Automated terminal flight correction",
                  "Survivability in contested EW environments",
                  "Human-in-the-loop oversight at all mission stages",
                  "PAL/NTSC switchable signal format",
                  "Dual-band wireless control with diversity antenna",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          </FadeIn>
        </div>
      </section>

      {/* ====== FEATURED PRODUCT 2: ADVANCED UAS PLATFORMS ====== */}
      <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40" data-testid="featured-product-uas">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          {/* Product Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-8 bg-[hsl(41,47%,61%)] rounded-sm" />
              <p
                className="text-xs font-semibold tracking-[0.14em] uppercase text-[hsl(41,47%,61%)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Featured Product
              </p>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Advanced UAS Platforms — Aguila
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-3xl">
              An advanced Unmanned Aerial System designed for reconnaissance and precision strike operations. The Aguila is engineered for operation in contested environments with AI-powered capabilities and proven government deployment across NATO member states.
            </p>
          </div>

          {/* Name Origin */}
          <div className="mb-10 max-w-3xl border-l-2 border-[hsl(41,47%,61%)] pl-5">
            <p className="text-base font-semibold tracking-tight text-foreground italic" style={{ fontFamily: 'var(--font-display)' }}>
              Aguila: Swift. Explosive. Inevitable.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Air (attack/seeker) — "Aguila", Spanish for "eagle." The eagle was special to the ancients; so much so that their elite warrior order was named after the majestic bird of prey — striking from above, fast and decisive.
            </p>
          </div>

          {/* Badges */}
          <div className="mb-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                AI Analysis & Recognition
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <Radar className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                Anti-Jamming / Anti-Spoofing
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <Users className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                Deployed with NATO MODs
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-[hsl(218,60%,10%)] rounded-lg">
              <Shield className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-display)' }}>
                Exclusive to Cortes Industries
              </span>
            </div>
          </div>

          {/* Main Image & Video */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Main Product Image */}
            <div className="rounded-lg overflow-hidden border border-border bg-secondary">
              <img
                src={UAS_MAIN_IMAGE}
                alt="Aguila Advanced UAS Platform"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Video */}
            <div className="rounded-lg overflow-hidden border border-border bg-black relative">
              {showUasVideo ? (
                <video
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                  data-testid="uas-video"
                >
                  <source src={UAS_VIDEO} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div
                  className="w-full h-full min-h-[320px] flex flex-col items-center justify-center cursor-pointer group"
                  onClick={() => setShowUasVideo(true)}
                  data-testid="uas-video-trigger"
                >
                  <div className="w-16 h-16 rounded-full bg-[hsl(41,47%,61%)] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-[hsl(218,60%,10%)] ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-white/80" style={{ fontFamily: 'var(--font-display)' }}>
                    Watch UAS Platform Demonstration
                  </p>
                  <p className="text-xs text-white/50 mt-1">Click to play</p>
                </div>
              )}
            </div>
          </div>

          {/* Platform Overview Cards with extracted images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Aguila Card */}
            <div className="border border-border rounded-lg overflow-hidden bg-white">
              <div className="aspect-[16/9] overflow-hidden bg-secondary">
                <img
                  src="/images/uas/sqa_lidar.png"
                  alt="Aguila with LIDAR sensor in flight"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <Plane className="w-5 h-5 text-[hsl(41,47%,61%)]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                      Aguila UAS
                    </h3>
                    <p className="text-xs text-[hsl(41,47%,61%)] font-medium">Launching 2026</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground mb-3">
                  Next-generation electric vertical take-off and landing system with state-of-the-art batteries, sensors, wiring, and propellers. Supports AI analysis and recognition for advanced aerial scouting and surveillance operations.
                </p>
                <ul className="space-y-1.5">
                  {[
                    "State-of-the-art batteries and propulsion systems",
                    "AI-powered analysis and target recognition",
                    "Integrated anti-jamming and anti-spoofing systems",
                    "GNSS-denied environment operation capability",
                    "LIDAR sensor integration for advanced terrain analysis",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                      <p className="text-xs text-muted-foreground">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>

          {/* Field Deployment Gallery */}
          <div className="mb-12">
            <h3
              className="text-lg font-semibold tracking-tight text-foreground mb-6"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Field Deployment & Operations
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg overflow-hidden border border-border bg-secondary">
                <img
                  src="/images/uas/sqa_military_display.png"
                  alt="Aguila military presentation"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>Military Evaluation</p>
                  <p className="text-xs text-muted-foreground">Aguila — Ministry of Defence presentation</p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden border border-border bg-secondary">
                <img
                  src="/images/uas/sqa_lidar.png"
                  alt="Aguila with LIDAR sensor in flight"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-xs font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>LIDAR Integration</p>
                  <p className="text-xs text-muted-foreground">Aguila — integrated LIDAR for terrain analysis</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Capabilities Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3
                className="text-lg font-semibold tracking-tight text-foreground mb-4"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Platform Capabilities
              </h3>
              <ul className="space-y-3">
                {[
                  "AI-driven analysis and target recognition systems",
                  "Integrated anti-jamming and anti-spoofing capabilities",
                  "GNSS-denied environment operation capability",
                  "LIDAR sensor integration for advanced terrain analysis",
                  "CBRN sensor pod capability for radiological, biological, and chemical detection",
                  "Proven deployment with NATO Ministry of Defence programs",
                  "Real-time encrypted telemetry and mission data relay",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3
                className="text-lg font-semibold tracking-tight text-foreground mb-4"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                C3P Command & Control Software
              </h3>
              <div className="p-5 bg-white rounded-lg border border-border mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Monitor className="w-4 h-4 text-[hsl(41,47%,61%)]" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                    Command, Communications, Control & Planning
                  </p>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  User-friendly touch screen interface enabling easy and rapid planning and execution of flight missions. Built-in failsafes prevent pilot error. Includes digital pre-flight checklist and flight simulator capability.
                </p>
              </div>
              <h4
                className="text-sm font-semibold text-foreground mb-3"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Proven Government Deployment
              </h4>
              <ul className="space-y-3">
                {[
                  "Ministry of Defence of Slovenia — active operational use",
                  "Ministry of Defence of Montenegro — Aguila handover completed",
                  "Ministry of Defence of Netherlands — tested and validated",
                  "Astraltrack antenna extends radio range up to 40 km for tracking",
                  "Frequently tested at military exercises, proving operational value",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ====== FEATURED PRODUCT 3: JAGWAR UGV ====== */}
      <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="featured-product-jagwar">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          <FadeIn>
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-8 bg-[hsl(41,47%,61%)] rounded-sm" />
              <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[hsl(41,47%,61%)]" style={{ fontFamily: 'var(--font-display)' }}>
                Featured Product
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
              Jagwar Autonomous Ground Platform
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-3xl">
              A modular unmanned ground vehicle engineered for persistent surveillance, perimeter defense, and precision strike operations. The Jagwar operates across contested terrain with minimal signature, delivering autonomous lethality and real-time intelligence in environments too dangerous for manned deployment.
            </p>
          </div>
          </FadeIn>

          <FadeIn delay={0.12}>
          <div className="mb-10 max-w-3xl border-l-2 border-[hsl(41,47%,61%)] pl-5">
            <p className="text-base font-semibold tracking-tight text-foreground italic" style={{ fontFamily: 'var(--font-display)' }}>
              Jagwar: Stalks. Strikes. Vanishes.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Land (attack/sentinel) — "Jaguar", the apex ground predator of the Americas: stealth, ambush, overwhelming close-quarters force.
            </p>
          </div>
          </FadeIn>

          <FadeIn delay={0.15}>
          <div className="mb-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-secondary border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>AI-Powered Autonomy</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-secondary border border-border rounded-lg">
              <Mountain className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>All-Terrain Capable</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-secondary border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>Fully Human Supervised</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-[hsl(218,60%,10%)] rounded-lg">
              <Shield className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-display)' }}>Exclusive to Cortes Industries</span>
            </div>
          </div>
          </FadeIn>

          <FadeIn delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            <div className="rounded-lg overflow-hidden border border-border bg-secondary">
              <img src={JAGWAR_IMAGE} alt="Jagwar Autonomous Ground Platform" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-lg overflow-hidden border border-border bg-[hsl(218,60%,10%)] flex items-center justify-center p-10">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(41,47%,61%)]/20 flex items-center justify-center mx-auto mb-6">
                  <Mountain className="w-8 h-8 text-[hsl(41,47%,61%)]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-display)' }}>Ground Dominance Platform</h3>
                <p className="text-sm text-white/70 leading-relaxed max-w-sm mx-auto">
                  Persistent surveillance and precision engagement across urban, desert, and mountainous terrain. Designed for operations where stealth and endurance outweigh speed.
                </p>
              </div>
            </div>
          </div>
          </FadeIn>

          <FadeIn delay={0.1}>
          <div className="mb-12">
            <h3 className="text-lg font-semibold tracking-tight text-foreground mb-6" style={{ fontFamily: 'var(--font-display)' }}>Technical Specifications</h3>
            <StaggerContainer stagger={0.06} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
              {JAGWAR_SPECS.map((spec) => (
                <StaggerItem key={spec.label} className="flex">
                <div className="border border-border rounded-lg p-4 bg-white spec-shimmer card-hover flex-1 flex flex-col">
                  <div className="flex items-center gap-2.5 mb-2">
                    <spec.icon className="w-4 h-4 text-[hsl(41,47%,61%)]" />
                    <span className="text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground" style={{ fontFamily: 'var(--font-display)' }}>{spec.label}</span>
                  </div>
                  <p className="text-base font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{spec.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{spec.detail}</p>
                </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
          </FadeIn>

          <FadeIn delay={0.1}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>System Capabilities</h3>
              <ul className="space-y-3">
                {[
                  "Autonomous patrol and perimeter defense with AI-driven threat detection",
                  "Multi-spectral sensor fusion: thermal imaging, LIDAR, and electro-optical systems",
                  "Modular payload bay supporting ISR, electronic warfare, and kinetic configurations",
                  "Silent electric drivetrain with low acoustic and thermal signature",
                  "GPS-denied navigation using inertial and terrain-matching systems",
                  "Swarm-capable mesh networking for coordinated multi-vehicle operations",
                  "All-terrain tracked mobility across urban rubble, sand, mud, and snow",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>Operational Modes</h3>
              <div className="p-5 bg-secondary/60 rounded-lg border border-border mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-foreground" style={{ fontFamily: 'var(--font-display)' }}>Sentinel Mode — Extended Autonomous Overwatch</p>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  In sentinel configuration, Jagwar maintains a static or slow-patrol posture for 48+ hours, providing continuous perimeter surveillance with autonomous threat classification and alert escalation. All engagement decisions remain under human authorization.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "Autonomous waypoint navigation and route planning",
                  "Real-time encrypted video and sensor data relay",
                  "Obstacle avoidance with dynamic path recalculation",
                  "Remote weapon station integration (optional)",
                  "Rapid field deployment — operational in under 15 minutes",
                  "Modular armor packages for threat-specific protection",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          </FadeIn>
        </div>
      </section>

      {/* ====== FEATURED PRODUCT 4: TIBRONE USV ====== */}
      <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40" data-testid="featured-product-tibrone">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          <FadeIn>
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-8 bg-[hsl(41,47%,61%)] rounded-sm" />
              <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[hsl(41,47%,61%)]" style={{ fontFamily: 'var(--font-display)' }}>
                Featured Product
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
              Tibrone Autonomous Maritime Platform
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-3xl">
              A high-speed unmanned surface vessel designed for persistent maritime patrol, coastal defense, and precision engagement operations. The Tibrone leverages a low-profile hull design and multi-domain sensor integration to conduct autonomous missions in contested littoral and open-water environments.
            </p>
          </div>
          </FadeIn>

          <FadeIn delay={0.12}>
          <div className="mb-10 max-w-3xl border-l-2 border-[hsl(41,47%,61%)] pl-5">
            <p className="text-base font-semibold tracking-tight text-foreground italic" style={{ fontFamily: 'var(--font-display)' }}>
              Tibrone: Wraith of the Waves.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Sea (attack/patrol) — "Tiburon" is Spanish for "shark." Silent, fast, lethal hunter. A nod to Spanish naval heritage from the conquest era.
            </p>
          </div>
          </FadeIn>

          <FadeIn delay={0.15}>
          <div className="mb-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>AI-Powered Navigation</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <Waves className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>All-Sea-State Capable</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-lg">
              <Radar className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: 'var(--font-display)' }}>Multi-Domain Sensors</span>
            </div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-[hsl(218,60%,10%)] rounded-lg">
              <Shield className="w-3.5 h-3.5 text-[hsl(41,47%,61%)]" />
              <span className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-display)' }}>Exclusive to Cortes Industries</span>
            </div>
          </div>
          </FadeIn>

          <FadeIn delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            <div className="rounded-lg overflow-hidden border border-border bg-secondary">
              <img src={TIBRONE_IMAGE} alt="Tibrone Autonomous Maritime Platform" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-lg overflow-hidden border border-border bg-[hsl(218,60%,10%)] flex items-center justify-center p-10">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(41,47%,61%)]/20 flex items-center justify-center mx-auto mb-6">
                  <Waves className="w-8 h-8 text-[hsl(41,47%,61%)]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-display)' }}>Maritime Dominance Platform</h3>
                <p className="text-sm text-white/70 leading-relaxed max-w-sm mx-auto">
                  Persistent autonomous patrol and precision engagement across littoral zones, port approaches, and open-water corridors. Designed for operations where endurance and stealth are mission-critical.
                </p>
              </div>
            </div>
          </div>
          </FadeIn>

          <FadeIn delay={0.1}>
          <div className="mb-12">
            <h3 className="text-lg font-semibold tracking-tight text-foreground mb-6" style={{ fontFamily: 'var(--font-display)' }}>Technical Specifications</h3>
            <StaggerContainer stagger={0.06} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
              {TIBRONE_SPECS.map((spec) => (
                <StaggerItem key={spec.label} className="flex">
                <div className="border border-border rounded-lg p-4 bg-white spec-shimmer card-hover flex-1 flex flex-col">
                  <div className="flex items-center gap-2.5 mb-2">
                    <spec.icon className="w-4 h-4 text-[hsl(41,47%,61%)]" />
                    <span className="text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground" style={{ fontFamily: 'var(--font-display)' }}>{spec.label}</span>
                  </div>
                  <p className="text-base font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{spec.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{spec.detail}</p>
                </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
          </FadeIn>

          <FadeIn delay={0.1}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>System Capabilities</h3>
              <ul className="space-y-3">
                {[
                  "Low-profile semi-submersible hull minimizing radar cross-section and visual detection",
                  "High-speed diesel-electric hybrid propulsion with silent running mode",
                  "Integrated sonar, marine radar, and electro-optical/infrared sensor suite",
                  "Autonomous collision avoidance and maritime traffic awareness (COLREGS compliant)",
                  "Modular mission bay supporting anti-surface, mine countermeasure, and ISR payloads",
                  "Encrypted satellite and line-of-sight communications for beyond-horizon operations",
                  "Self-righting hull design with operational capability up to Sea State 5",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>Mission Profiles</h3>
              <div className="p-5 bg-white rounded-lg border border-border mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-foreground" style={{ fontFamily: 'var(--font-display)' }}>Persistent Patrol — 72-Hour Autonomous Endurance</p>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  In patrol configuration, Tibrone maintains continuous autonomous coverage of designated maritime zones for 72+ hours, providing real-time threat detection and classification. Engagement authorization remains under human control via encrypted command link.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "Coastal and port security — approach monitoring and threat interception",
                  "Anti-submarine warfare — towed sonar array and acoustic detection",
                  "Mine countermeasures — autonomous survey and identification",
                  "Maritime interdiction — high-speed pursuit and boarding support",
                  "ISR relay — persistent intelligence collection and data fusion",
                  "Force protection — escort and perimeter defense for high-value assets",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                    <p className="text-sm leading-relaxed text-muted-foreground">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          </FadeIn>
        </div>
      </section>

      <RevealLine />
      <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Additional Offerings"
            title="Products & Services"
            description="Procurement opportunities, industrial capabilities, and strategic sourcing programs exclusive to the Cortes Industries network."
            className="mb-10"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr" data-testid="featured-grid">
            {OTHER_PRODUCTS.map((product) => (
              <div
                key={product.title}
                className="border border-border rounded-lg p-6 bg-white card-hover group flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <product.icon className="w-4 h-4 text-[hsl(41,47%,61%)]" />
                  </div>
                  <span
                    className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {product.category}
                  </span>
                </div>
                <h3
                  className="text-base font-semibold tracking-tight text-foreground mb-2"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {product.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground mb-4 flex-1">
                  {product.description}
                </p>
                <ul className="space-y-1.5 mt-auto">
                  {product.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                      <p className="text-xs text-muted-foreground">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Count on Cortes */}
      <section className="py-14 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Count on Cortes"
            title="When Programs Demand Certainty"
            description="Cortes Industries is built for environments where outcomes matter and conditions are demanding. All products and services listed are exclusive to the Cortes Industries network."
            align="center"
            className="mb-10"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              "Restricted or sensitive supply chains",
              "Multi-agency coordination and oversight",
              "Accelerated timelines with little tolerance for slip",
              "Novel technologies or in-demand materials",
              "Program shifts requiring rapid alignment",
              "External scrutiny or high-visibility environments",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-4 bg-secondary/60 border border-border rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                <p className="text-sm text-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactBanner />
    </div>
    </PageTransition>
  );
}
